/**
 * Needs answers (doc 07, screen 5): one card per thing waiting, with its fix
 * next to it.
 */

import { useState } from "react";
import { Bot, CircleHelp, PauseCircle, Plus, RefreshCw, Rows3, TriangleAlert } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { RecordPicker } from "@/components/integrations/RecordPicker";
import {
  AgentPanel,
  AllClear,
  AnswerCard,
  Banner,
  Button,
  Column,
  Disclosure,
  Input,
  LinkButton,
  Panel,
  SkeletonRows,
  Table,
  Td,
  Th,
} from "@/components/kit";
import {
  answerQuestion,
  decideApproval,
  linkId,
  listAnswers,
  listLinks,
  readApprovals,
  readChat,
  replayRun,
  scoutJobUrl,
  sendChat,
  type Answer,
  type Approval,
  type IntegrationDetail,
} from "@/lib/integrations/api";
import { showValue, valueForType } from "@/lib/integrations/settings";
import { answerKindWords, when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useKeyed } from "../hooks";

/** What "all clear" means: every kind of thing that would show here with its fix. */
const WATCHING = [
  { title: "Rows that find no record", note: "Held for a person to link or create." },
  { title: "New values", note: "A value that a map does not know yet." },
  { title: "Scout’s columns", note: "A column that is new, gone, or empty in every row." },
  { title: "Guards", note: "A run paused before it could write bad data." },
  { title: "Scout’s agent", note: "Its questions, and scraper changes to approve." },
  { title: "The workflow’s import", note: "New fields that must be mapped." },
  { title: "Id links", note: "Their ids with no record linked." },
];

function theirId(answer: Answer): string | null {
  const value = answer.payload.value as Record<string, unknown> | undefined;
  const found = value?.externalId ?? value?.id ?? answer.payload.key;
  return found === null || found === undefined ? null : String(found);
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

function listWords(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** What a column question says: its title and one or two sentences under it. */
function columnWords(answer: Answer): { title: string; body: string } {
  const column = String(answer.payload.column ?? answer.key);
  const fields = Array.isArray(answer.payload.fields) ? answer.payload.fields.map(String) : [];
  const columns = Array.isArray(answer.payload.columns) ? answer.payload.columns.map(String) : [];
  const rows = Number(answer.payload.rows ?? 0);
  const readers = fields.length > 0 ? `${listWords(fields)} ${fields.length === 1 ? "reads" : "read"} it` : "A field reads it";
  if (answer.kind === "new_column") {
    const filled = Number(answer.payload.filled ?? 0);
    const inside = columns.length > 1 ? ` It holds ${columns.length} columns, such as ${columns.slice(0, 3).join(", ")}.` : "";
    return {
      title: `Scout sent a new column: ${column}`,
      body: `It has a value in ${filled.toLocaleString()} of ${plural(rows, "row")}. Nothing reads it yet.${inside}`,
    };
  }
  const first = columns.length > 1 ? ` The field takes the first of ${listWords(columns)} that has a value.` : "";
  if (answer.kind === "missing_column") {
    const before = answer.payload.before === true ? " It was there in the run before." : "";
    return {
      title: `Scout no longer sends ${column}`,
      body: `${readers}, so ${fields.length > 1 ? "those fields come" : "it comes"} back empty. If a required field reads it, its rows are refused.${before}${first}`,
    };
  }
  return {
    title: `${column} came back empty in every row`,
    body: `${readers}. None of the ${plural(rows, "row")} had a value.${first}`,
  };
}

/** "Status sent 2 new values": their values, how many rows had each, and a box for ours. */
function NewValuesCard({
  answer,
  timeZone,
  disabled,
  onAdd,
  onDefault,
}: {
  answer: Answer;
  timeZone: string;
  disabled: boolean;
  onAdd: (values: Record<string, unknown>) => void;
  onDefault: () => void;
}) {
  const [ours, setOurs] = useState<Record<string, string>>({});
  const values = (Array.isArray(answer.payload.values) ? answer.payload.values : []) as { value: string; rows: number }[];
  const more = Number(answer.payload.more ?? 0);
  const total = values.length + more;
  const label = String(answer.payload.label ?? answer.payload.field ?? "A field");
  const type = typeof answer.payload.type === "string" ? answer.payload.type : undefined;
  const typed = Object.entries(ours).filter(([, text]) => text.trim() !== "");
  const did = String(answer.payload.did ?? "");
  const said = answer.payload.otherwise === "default" ? `${did.replace(/\.$/, "")}: ${showValue(answer.payload.default)}.` : did;
  return (
    <AnswerCard
      tone="warning"
      icon={<TriangleAlert size={16} aria-hidden />}
      title={`${label} sent ${plural(total, "value")} not in its map`}
      rows={values.reduce((sum, one) => sum + one.rows, 0)}
      meta={`${answerKindWords(answer.kind)} · ${when(answer.createdAt, timeZone)}`}
      actions={
        <>
          <Button
            tone="primary"
            disabled={disabled || typed.length === 0}
            onClick={() => onAdd(Object.fromEntries(typed.map(([their, text]) => [their, valueForType(text, type)])))}
          >
            <Plus size={14} aria-hidden />
            Add to the map
          </Button>
          <Button disabled={disabled} onClick={onDefault}>
            Use the default
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--s2)" }}>
        <span style={{ fontSize: "var(--t-sm)" }}>
          {said} Type our value for each one you want to add. They go into the draft; publish it to use them.
        </span>
        <Table>
          <thead>
            <tr>
              <Th>Their value</Th>
              <Th num>Rows</Th>
              <Th>Our value</Th>
            </tr>
          </thead>
          <tbody>
            {values.map((one) => (
              <tr key={`value-${one.value}`}>
                <Td>{one.value === "" ? "(empty)" : one.value}</Td>
                <Td num>{one.rows.toLocaleString()}</Td>
                <Td>
                  <Input
                    aria-label={`Our value for ${one.value === "" ? "an empty value" : one.value}`}
                    value={ours[one.value] ?? ""}
                    disabled={disabled}
                    onChange={(event) => setOurs({ ...ours, [one.value]: event.target.value })}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {more > 0 && <span style={{ fontSize: "var(--t-sm)", color: "var(--ink-3)" }}>{plural(more, "more value")} not listed here.</span>}
      </div>
    </AnswerCard>
  );
}

export function AnswersTab({ item, canWrite, onChanged }: { item: IntegrationDetail; canWrite: boolean; onChanged: () => void }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const open = useKeyed(`answers|${item.code}|${item.openAnswers}`, () => listAnswers(item.code, "open"));
  const past = useKeyed(`answers-past|${item.code}|${item.openAnswers}`, () => listAnswers(item.code, "all"));
  const approvals = useKeyed(item.scoutJobId ? `approvals|${item.code}` : null, () => readApprovals(item.code));
  const chat = useKeyed(item.scoutJobId ? `chat|${item.code}` : null, () => readChat(item.code));
  const missing = useKeyed(`missing|${item.code}`, () => listLinks(item.code, { entity: "sport", limit: 1 }));
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<{ tone: "good" | "danger" | "warning"; text: string; replay?: string } | null>(null);
  const [asking, setAsking] = useState<{ answer: Answer; kind: "write" } | null>(null);
  const navigate = useNavigate();

  const reload = () => {
    open.reload();
    past.reload();
    onChanged();
  };

  /** Do an answer, then say what happened. ``done`` may read omnium's own words from the result. */
  const act = (
    key: string,
    work: () => Promise<unknown>,
    done: string | ((result: unknown) => string),
    replay?: string,
    then?: () => void,
  ) => {
    setBusy(key);
    work()
      .then(
        (result) => {
          setSaid({ tone: "good", text: typeof done === "string" ? done : done(result), replay });
          reload();
          then?.();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not take that answer") }),
      )
      .finally(() => {
        setBusy(null);
        setAsking(null);
      });
  };

  const decide = (approval: Approval, decision: { approved?: boolean; answer?: string }) => {
    setBusy(approval.id);
    decideApproval(item.code, approval.id, decision)
      .then(
        () => {
          approvals.reload();
          chat.reload();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "Scout did not take the answer") }),
      )
      .finally(() => setBusy(null));
  };

  const cards = open.data ?? [];
  const needsUpdate = item.needsUpdate ?? [];
  const missingLinks = missing.data?.missing ?? [];
  const nothing = cards.length === 0 && needsUpdate.length === 0 && (approvals.data ?? []).length === 0 && missingLinks.length === 0;
  const settingsHref = `/integrations/${encodeURIComponent(item.code)}/settings`;

  return (
    <Column>
      {said && (
        <Banner
          tone={said.tone}
          actions={
            <>
              {said.replay && canWrite && (
                <Button
                  onClick={() => {
                    const run = said.replay ?? "";
                    act("replay", () => replayRun(item.code, { run }), "Queued. The run is sent again with the live version.");
                  }}
                >
                  Send that run again
                </Button>
              )}
              <Button tone="subtle" onClick={() => setSaid(null)}>
                Close
              </Button>
            </>
          }
        >
          {said.text}
        </Banner>
      )}
      {asking && (
        <Banner
          tone="warning"
          actions={
            <>
              <Button
                tone="primary"
                disabled={busy !== null}
                onClick={() => {
                  const run = String(asking.answer.payload.run ?? asking.answer.runId ?? "");
                  act(
                    asking.answer.id,
                    () =>
                      answerQuestion(item.code, asking.answer.id, { answer: { resume: true, writeAnyway: true } }).then(() =>
                        run ? replayRun(item.code, { run }) : null,
                      ),
                    "Switched back on, and the paused run is queued to be written.",
                  );
                }}
              >
                {busy ? "Working…" : "Yes, write it"}
              </Button>
              <Button tone="subtle" disabled={busy !== null} onClick={() => setAsking(null)}>
                Cancel
              </Button>
            </>
          }
        >
          Write the paused run anyway? Its rows go into {item.eventName} as they are, and the integration switches back on.
        </Banner>
      )}

      {open.error && <Banner tone="danger" actions={<Button onClick={open.reload}>Try again</Button>}>{open.error}</Banner>}
      {!open.data && !open.error && <SkeletonRows rows={3} columns={2} />}

      {open.data && nothing && (
        <AllClear
          title="All clear"
          note="Nothing is waiting for you. omnium keeps watching these, and anything it finds shows here with its fix."
          watching={WATCHING}
        />
      )}

      {needsUpdate.length > 0 && (
        <AnswerCard
          tone="warning"
          icon={<RefreshCw size={16} aria-hidden />}
          title={`The workflow changed ${item.importCommand}: ${needsUpdate.join(", ")} must now come from somewhere.`}
          actions={<LinkButton to={settingsHref} tone="primary">Map it now</LinkButton>}
        >
          It keeps running on the version it was built for until the new fields are mapped and published.
        </AnswerCard>
      )}

      {cards.map((answer) => {
        if (answer.kind === "guard_pause") {
          const run = String(answer.payload.run ?? answer.runId ?? "");
          return (
            <AnswerCard
              key={answer.id}
              tone="danger"
              icon={<PauseCircle size={16} aria-hidden />}
              title={`Paused: ${String(answer.payload.message ?? "a safety guard stopped it")}`}
              meta={`${answerKindWords(answer.kind)} · ${when(answer.createdAt, timeZone)}`}
              actions={
                <>
                  {run && <LinkButton to={`/integrations/${encodeURIComponent(item.code)}/runs/${run}`}>Look at the run</LinkButton>}
                  <Button tone="primary" disabled={!canWrite || busy !== null} onClick={() => setAsking({ answer, kind: "write" })}>
                    Write anyway
                  </Button>
                  <Button
                    disabled={!canWrite || busy !== null}
                    onClick={() => act(answer.id, () => answerQuestion(item.code, answer.id, { dismiss: true }), "It stays paused. Resume it when the source is fixed.")}
                  >
                    Stay paused
                  </Button>
                </>
              }
            />
          );
        }
        if (answer.kind === "held_row") {
          const their = theirId(answer);
          const value = (answer.payload.value ?? {}) as Record<string, unknown>;
          const run = answer.runId ?? undefined;
          return (
            <AnswerCard
              key={answer.id}
              tone="warning"
              icon={<Rows3 size={16} aria-hidden />}
              rows={1}
              title={`Row${showValue(answer.payload.key ?? answer.payload.position)} is waiting: ${String(answer.payload.why ?? "no record fits it")}`}
              meta={`${answerKindWords(answer.kind)} · ${when(answer.createdAt, timeZone)}`}
              actions={
                <>
                  {their && (
                    <RecordPicker
                      code={item.code}
                      entity="participant"
                      disabled={!canWrite}
                      busy={busy === answer.id}
                      onPick={(hit, entity) =>
                        act(
                          answer.id,
                          () =>
                            linkId(item.code, { entity, externalId: their, recordId: hit.id }).then(() =>
                              answerQuestion(item.code, answer.id, { answer: { linked: hit.id, entity, externalId: their } }),
                            ),
                          `Linked ${their} to ${hit.name ?? hit.code}. Next time it matches by id.`,
                          run,
                        )
                      }
                    />
                  )}
                  {answer.payload.canCreate === true && (
                    <Button
                      disabled={!canWrite || busy !== null}
                      onClick={() =>
                        act(
                          answer.id,
                          () => answerQuestion(item.code, answer.id, { answer: { create: true } }),
                          (result) => (result as Answer).message ?? "Saved. The next run makes a new record for this row.",
                          run,
                        )
                      }
                    >
                      <Plus size={15} aria-hidden />
                      Create a new one
                    </Button>
                  )}
                  <Button
                    disabled={!canWrite || busy !== null}
                    onClick={() => act(answer.id, () => answerQuestion(item.code, answer.id, { dismiss: true }), "The row is skipped.")}
                  >
                    Skip this row
                  </Button>
                </>
              }
            >
              {Object.keys(value).length > 0 && (
                <span style={{ fontSize: "var(--t-sm)" }}>
                  {Object.entries(value)
                    .slice(0, 6)
                    .map(([key, one]) => `${key}: ${showValue(one, 40)}`)
                    .join(" · ")}
                </span>
              )}
            </AnswerCard>
          );
        }
        if (answer.kind === "new_values") {
          return (
            <NewValuesCard
              key={answer.id}
              answer={answer}
              timeZone={timeZone}
              disabled={!canWrite || busy !== null}
              onAdd={(values) =>
                act(
                  answer.id,
                  () => answerQuestion(item.code, answer.id, { answer: { values } }),
                  (result) => (result as Answer).message ?? "Added to the map in the draft. Publish the draft to use it.",
                )
              }
              onDefault={() =>
                act(
                  answer.id,
                  () => answerQuestion(item.code, answer.id, { dismiss: true }),
                  "Left as it is. Rows with these values follow the map's “anything else” setting.",
                )
              }
            />
          );
        }
        if (answer.kind === "new_column" || answer.kind === "missing_column" || answer.kind === "empty_column") {
          const words = columnWords(answer);
          const isNew = answer.kind === "new_column";
          const ignored = isNew
            ? "Ignored. You are not asked about this column again."
            : answer.kind === "empty_column"
              ? "Ignored for this run. If it has values again and then comes back empty, you are asked again."
              : "Ignored. If it comes back and then goes missing again, you are asked again.";
          return (
            <AnswerCard
              key={answer.id}
              tone="warning"
              icon={isNew ? <Plus size={16} aria-hidden /> : <TriangleAlert size={16} aria-hidden />}
              title={words.title}
              rows={isNew ? undefined : Number(answer.payload.rows ?? 0) || undefined}
              meta={`${answerKindWords(answer.kind)} · ${when(answer.createdAt, timeZone)}`}
              actions={
                <>
                  <Button
                    tone="primary"
                    disabled={!canWrite || busy !== null}
                    onClick={() =>
                      act(
                        answer.id,
                        () => answerQuestion(item.code, answer.id, { answer: { change: true } }),
                        "Change it in Settings, then publish the draft.",
                        undefined,
                        () => navigate(settingsHref),
                      )
                    }
                  >
                    {isNew ? "Map it" : "Change the column"}
                  </Button>
                  <Button
                    disabled={!canWrite || busy !== null}
                    onClick={() => act(answer.id, () => answerQuestion(item.code, answer.id, { dismiss: true }), ignored)}
                  >
                    {answer.kind === "empty_column" ? "Ignore for this run" : "Ignore"}
                  </Button>
                </>
              }
            >
              {words.body}
            </AnswerCard>
          );
        }
        return (
          <AnswerCard
            key={answer.id}
            tone="info"
            icon={<CircleHelp size={16} aria-hidden />}
            title={answerKindWords(answer.kind)}
            meta={when(answer.createdAt, timeZone)}
            actions={
              <Button disabled={!canWrite || busy !== null} onClick={() => act(answer.id, () => answerQuestion(item.code, answer.id, { dismiss: true }), "Dismissed.")}>
                Dismiss
              </Button>
            }
          >
            {JSON.stringify(answer.payload)}
          </AnswerCard>
        );
      })}

      {missingLinks.length > 0 && (
        <AnswerCard
          tone="warning"
          icon={<Rows3 size={16} aria-hidden />}
          title={`${missingLinks.length} of their sport ids are not linked: ${missingLinks.slice(0, 5).map((one) => one.externalId).join(", ")}`}
          rows={missingLinks.reduce((sum, one) => sum + one.rows, 0)}
          actions={<LinkButton to={`/integrations/${encodeURIComponent(item.code)}/links`}>Pick the records</LinkButton>}
        >
          Rows with these ids cannot be read until each is linked to one of our sports.
        </AnswerCard>
      )}

      {item.scoutJobId && (
        <Panel title="Scout’s agent" icon={<Bot size={18} aria-hidden />} note="Its questions, the scraper changes it wants approved, and what it said.">
          <AgentPanel
            messages={chat.data}
            approvals={approvals.data}
            loading={chat.busy || approvals.busy}
            error={chat.error ?? approvals.error ?? null}
            busy={busy}
            scoutUrl={scoutJobUrl(item.scoutJobId, true)}
            onRetry={() => {
              chat.reload();
              approvals.reload();
            }}
            onReply={(text) => {
              setBusy("reply");
              sendChat(item.code, text)
                .then(() => chat.reload(), (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "Scout did not take the message") }))
                .finally(() => setBusy(null));
            }}
            onDecide={decide}
          />
        </Panel>
      )}

      {(past.data ?? []).some((one) => one.status !== "open") && (
        <Disclosure title="Answered before" count={(past.data ?? []).filter((one) => one.status !== "open").length}>
          <ul style={{ margin: 0, paddingLeft: "var(--s4)", display: "grid", gap: "var(--s1)" }}>
            {(past.data ?? [])
              .filter((one) => one.status !== "open")
              .map((one) => (
                <li key={one.id}>
                  {answerKindWords(one.kind)} · {one.key} · {one.status} by {one.answeredBy ?? "someone"} {when(one.answeredAt, timeZone)}
                </li>
              ))}
          </ul>
        </Disclosure>
      )}
      <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
        Held rows and guard pauses come from runs. <Link to={`/integrations/${encodeURIComponent(item.code)}/runs`}>See the runs</Link>
      </p>
    </Column>
  );
}
